import type { BaseResponse } from '@core/services/api/api.types';

/** ERP send endpoints (`logo-.../send`) report each row's failure in `erp_send_result_dto`. */
interface ErpSendRow {
  erp_send_result_dto?: { message?: string };
}

/** Gateway error bodies follow the same envelope as a successful `BaseResponse`, plus an optional plain `message`. */
type ApiFailBody = BaseResponse<ErpSendRow[]> & { message?: string };

interface HttpStyleError {
  error?: ApiFailBody;
  message?: string;
}

/** Gateway `iomessage` entries can carry a raw serialized exception dump instead of a readable line. */
function isStackTraceMessage(text: string): boolean {
  const trimmed = text.trim();
  return trimmed.startsWith('{') && (trimmed.includes('stackTrace') || trimmed.includes('"cause"'));
}

/** Reads the best user-facing text directly out of a gateway response/error body (no HTTP-error wrapper). */
export function messageFromApiBody(body: object): string | null {
  const apiBody = body as ApiFailBody;

  const ui = apiBody.uimessage;
  if (Array.isArray(ui)) {
    for (const entry of ui) {
      if (entry?.text && !isStackTraceMessage(entry.text)) {
        return String(entry.text);
      }
    }
  }

  const io = apiBody.iomessage;
  if (Array.isArray(io)) {
    for (const entry of io) {
      if (entry?.type === 'error' && entry?.text && !isStackTraceMessage(entry.text)) {
        return String(entry.text);
      }
    }
    for (const entry of io) {
      if (entry?.text && !isStackTraceMessage(entry.text)) {
        return String(entry.text);
      }
    }
  }

  if (Array.isArray(apiBody.data)) {
    const erpMessages = new Set<string>();
    for (const row of apiBody.data) {
      const text = row?.erp_send_result_dto?.message;
      if (text) {
        erpMessages.add(text);
      }
    }
    if (erpMessages.size) {
      return [...erpMessages].join('\n');
    }
  }

  if (typeof apiBody.message === 'string' && apiBody.message && !isStackTraceMessage(apiBody.message)) {
    return apiBody.message;
  }
  return null;
}

/** True when a 200-level gateway response body itself represents a business-rule failure. */
export function isApiFailure(response: object): boolean {
  if (!('outcome_type' in response)) {
    return false;
  }
  const outcome = (response as ApiFailBody).outcome_type;
  return outcome === 'fail' || outcome === 'error';
}

/**
 * Best-effort user-facing message from a gateway error: prefers `error.uimessage`, then
 * `error.iomessage` (type `error` first), then each `error.data[].erp_send_result_dto.message`,
 * then `error.message`, then the outer HTTP message.
 * Falls back to `fallback` (an i18n key) when nothing usable is found.
 */
export function extractApiErrorMessage(error: object | Error | string, fallback = 'an_error_occurred'): string {
  if (typeof error === 'string') {
    return error || fallback;
  }
  if (error instanceof Error) {
    return error.message || fallback;
  }
  const parsed = error as HttpStyleError;
  const fromBody =
    (parsed.error && messageFromApiBody(parsed.error)) ??
    messageFromApiBody(parsed) ??
    parsed.error?.message ??
    parsed.message;
  return fromBody ?? fallback;
}
